/** Day arithmetic for the transactions view.
 *
 *  Everything here works in **UTC**, deliberately: the server buckets a
 *  day as UTC midnight-to-midnight (same as the admin transactions
 *  page), so doing the arithmetic in local time would put the boundary
 *  in a different place than the query does and the last sale of the
 *  evening would appear to vanish.
 *
 *  The tradeoff: in Atlantic time (UTC-3/-4) a sale rung up after
 *  ~8–9pm local falls into the *next* UTC day. The store closes before
 *  that, so it doesn't bite in practice — but it's the reason a late
 *  test sale can look like it landed on tomorrow.
 */

const MS_PER_DAY = 86_400_000;

/** `YYYY-MM-DD` for a Date, in UTC. */
export function toIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Parse `YYYY-MM-DD` to a UTC-midnight Date. Returns null on junk so
 *  callers can fall back rather than compute with an Invalid Date. */
export function fromIsoDay(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const ms = Date.parse(`${iso}T00:00:00Z`);
  return isNaN(ms) ? null : new Date(ms);
}

export function todayIso(): string {
  return toIsoDay(new Date());
}

/** Shift an ISO day by `n` days (negative goes back). Invalid input is
 *  returned unchanged. */
export function addDays(iso: string, n: number): string {
  const d = fromIsoDay(iso);
  if (!d) return iso;
  return toIsoDay(new Date(d.getTime() + n * MS_PER_DAY));
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const a = fromIsoDay(from);
  const b = fromIsoDay(to);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
}

/** The oldest day the till may browse. `windowDays === 0` means no
 *  limit, in which case there's no floor to return. */
export function windowFloor(today: string, windowDays: number): string | null {
  if (windowDays <= 0) return null;
  return addDays(today, -windowDays);
}

/** Mirrors the server's `day_within_window`. Kept in step deliberately —
 *  the UI clamp is convenience, the server call is the control. */
export function withinWindow(
  day: string,
  today: string,
  windowDays: number,
): boolean {
  if (windowDays <= 0) return true;
  const delta = daysBetween(day, today);
  // Negative delta means `day` is in the future.
  return delta >= 0 && delta <= windowDays;
}

/** "Wed, Aug 12, 2026" — rendered in UTC to match the bucketing. */
export function formatDayLabel(iso: string): string {
  const d = fromIsoDay(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "3:31p" — compact enough for a dense transaction list. Rendered in
 *  the browser's local zone, because that's the clock the cashier was
 *  looking at when the sale happened. */
export function formatTime(rfc3339: string | null): string {
  if (!rfc3339) return '—';
  const ms = Date.parse(rfc3339);
  if (isNaN(ms)) return '—';
  const d = new Date(ms);
  const h24 = d.getHours();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}${h24 < 12 ? 'a' : 'p'}`;
}
